/**
 * Боксы детектора (yolov5-freezer) на отменённых фото — правка руками (Алексей 29.09, AI191).
 *
 * Очередь — отменённые валидатором алерты YOLO-стеллажей: модель сказала «пусто», человек
 * ответил «полная». Работа идёт на КРОПЕ СТЕЛЛАЖА (то, что видел детектор), но карточка по
 * умолчанию показывает крупно ту полку, из-за которой сработал алерт: боксы и полки лежат в
 * одних долях 0–1 этого кропа, поэтому «правка на полке» и есть правка на стеллаже —
 * переносить ничего не нужно, цифры 1…9 переключают полку, 0 — весь стеллаж.
 *
 * Мышь, как у полигонов в зонировании: клик выбирает бокс; повторный клик в той же точке —
 * следующий из наложенных (цикл идёт от ВЫБРАННОГО, а не от верхнего, иначе третий в стопке
 * недостижим); Ctrl/⌘ + рамка — выделить всё в области; просто рамка на пустом месте — новый
 * бокс (ложное «пусто» — это чаще НЕДОСТАЮЩИЙ бокс, чем лишний; без дорисовки такие фото
 * нечем исправить). Клавиши — по коду, а не по символу, чтобы работали и в русской раскладке.
 *
 * Правки не уходят сами: изменённые карточки копятся синими, и одна кнопка «Сохранить»
 * отправляет всю пачку; «Разметка корректная (N)» подтверждает нетронутые фото страницы.
 */
import { Select } from '@/components/ui/select';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  CheckCheck, ChevronLeft, ChevronRight, Loader2, RefreshCw, Save, Undo2, ZoomIn, ZoomOut,
} from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import {
  BOX_VIEWS, boxPoolAnnotate, getBoxPoolSamples, getBoxPoolZones,
  type Box, type BoxItem, type BoxLabelDelta, type BoxView,
} from '@/api/boxPool';
import { Tooltip } from '@/components/ui/tooltip';

const PAGE_SIZE = 24;
const MIN_COLS = 1;
const MAX_COLS = 4;
const DAYS_OPTIONS = [7, 14, 30, 60, 120];
/** Запас над и под полкой при зуме — доля её высоты: товар выступает за полигон. */
const PAD = 0.2;
/** Меньше этого (в долях кропа) — не бокс, а дрожание руки. */
const MIN_BOX = 0.01;
/** До этого сдвига в пикселях нажатие считается кликом, не рамкой. */
const CLICK_PX = 5;

type Pt = { x: number; y: number };
type Rect = { x0: number; y0: number; x1: number; y1: number };
type Dims = { w: number; h: number };
type Drag = {
  mode: 'draw' | 'select' | 'click'; start: Pt; startPx: Pt; cur: Pt; moving: boolean; shift: boolean;
};

const RU_STATUS: Record<string, string> = {
  FULL: 'полная', TIDY_BUT_LOW_STOCK: 'аккуратно, мало', PARTIALLY_EMPTY: 'частично пусто',
  NOTICEABLY_EMPTY: 'заметно пусто', ALMOST_EMPTY: 'почти пусто',
};
const statusRu = (s: string | null) => (s ? RU_STATUS[s] ?? s : '—');

const area = (b: Rect) => Math.max(0, b.x1 - b.x0) * Math.max(0, b.y1 - b.y0);
const contains = (b: Rect, p: Pt) => p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1;
const intersects = (a: Rect, b: Rect) => a.x0 <= b.x1 && a.x1 >= b.x0 && a.y0 <= b.y1 && a.y1 >= b.y0;
const rectOf = (a: Pt, b: Pt): Rect => ({
  x0: Math.min(a.x, b.x), y0: Math.min(a.y, b.y), x1: Math.max(a.x, b.x), y1: Math.max(a.y, b.y),
});
const sameBoxes = (a: Box[], b: Box[]) => JSON.stringify(a) === JSON.stringify(b);
const fmtTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Окно просмотра: весь кроп или полка с запасом. */
function viewOf(it: BoxItem, shelfId: number | null): Rect {
  const sh = shelfId == null ? null : it.shelves.find(s => s.id === shelfId);
  if (!sh?.poly) return { x0: 0, y0: 0, x1: 1, y1: 1 };
  const xs = sh.poly.map(p => p[0]);
  const ys = sh.poly.map(p => p[1]);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const pad = (y1 - y0) * PAD;
  return {
    x0: Math.max(0, Math.min(...xs) - 0.01), x1: Math.min(1, Math.max(...xs) + 0.01),
    y0: Math.max(0, y0 - pad), y1: Math.min(1, y1 + pad),
  };
}

/** Полка, из-за которой сработал алерт, — с неё и начинаем; иначе весь стеллаж. */
const defaultShelf = (it: BoxItem) => it.shelves.find(s => s.empty)?.id ?? null;

const boxStyle = (b: Box, selected: boolean) => {
  if (selected) return { stroke: '#2563eb', strokeWidth: 3, fill: 'rgba(37,99,235,0.22)', dash: undefined };
  if (b.deleted) return { stroke: '#ef4444', strokeWidth: 1.5, fill: 'rgba(239,68,68,0.12)', dash: '6 4' };
  if (b.added) return { stroke: '#a855f7', strokeWidth: 2, fill: 'rgba(168,85,247,0.12)', dash: undefined };
  if (b.weak) return { stroke: '#9ca3af', strokeWidth: 1, fill: 'rgba(156,163,175,0.08)', dash: '3 3' };
  return { stroke: '#22c55e', strokeWidth: 2, fill: 'rgba(34,197,94,0.08)', dash: undefined };
};

// ── card ────────────────────────────────────────────────────────────────────────

interface CardProps {
  it: BoxItem;
  boxes: Box[];
  selected: Set<number>;
  shelfId: number | null;
  dims: Dims | undefined;
  focused: boolean;
  pending: 'modified' | 'correct' | null;
  showWeak: boolean;
  onFocus: () => void;
  onSelect: (sel: Set<number>) => void;
  onChange: (boxes: Box[]) => void;
  onShelf: (shelfId: number | null) => void;
  onUnlabel?: () => void;
}

const BoxCard = memo(function BoxCard({
  it, boxes, selected, shelfId, dims, focused, pending, showWeak, onFocus, onSelect, onChange, onShelf, onUnlabel,
}: CardProps) {
  const { t } = useT();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const drag = useRef<Drag | null>(null);
  const lastClick = useRef<{ px: Pt; cands: number[] } | null>(null);
  const [band, setBand] = useState<Rect | null>(null);

  const view = viewOf(it, shelfId);
  const vw = view.x1 - view.x0;
  const vh = view.y1 - view.y0;
  const aspect = dims ? `${vw * dims.w} / ${vh * dims.h}` : `${vw * 2} / ${vh * 3}`;
  const visible = (b: Box) => showWeak || !b.weak;

  const toPt = (e: React.PointerEvent): Pt => {
    const r = svgRef.current!.getBoundingClientRect();
    return { x: view.x0 + ((e.clientX - r.left) / r.width) * vw, y: view.y0 + ((e.clientY - r.top) / r.height) * vh };
  };

  const onDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    onFocus();
    const p = toPt(e);
    const hit = boxes.some((b, i) => visible(b) && contains(b, p) && i >= 0);
    drag.current = {
      mode: e.ctrlKey || e.metaKey ? 'select' : hit ? 'click' : 'draw',
      start: p, startPx: { x: e.clientX, y: e.clientY }, cur: p, moving: false, shift: e.shiftKey,
    };
    svgRef.current?.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    d.cur = toPt(e);
    if (!d.moving && Math.hypot(e.clientX - d.startPx.x, e.clientY - d.startPx.y) > CLICK_PX) d.moving = true;
    if (d.moving && d.mode !== 'click') setBand(rectOf(d.start, d.cur));
  };

  const onUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    setBand(null);
    if (!d) return;
    const p = toPt(e);
    if (!d.moving) {
      // Клик. Кандидаты — все боксы под курсором, мелкие первыми: наложенный маленький
      // иначе никогда не выбрать.
      const cands = boxes.map((b, i) => ({ b, i })).filter(({ b }) => visible(b) && contains(b, p))
        .sort((a, c) => area(a.b) - area(c.b)).map(({ i }) => i);
      if (!cands.length) { onSelect(new Set()); lastClick.current = null; return; }
      if (d.shift) {
        const next = new Set(selected);
        if (next.has(cands[0])) next.delete(cands[0]); else next.add(cands[0]);
        onSelect(next);
        return;
      }
      const same = lastClick.current
        && Math.hypot(lastClick.current.px.x - e.clientX, lastClick.current.px.y - e.clientY) <= CLICK_PX * 2;
      const cur = selected.size === 1 ? [...selected][0] : -1;
      const at = cands.indexOf(cur);
      // Второй клик в той же точке — следующий из наложенных, по кругу от выбранного.
      const pick = same && at >= 0 ? cands[(at + 1) % cands.length] : cands[0];
      lastClick.current = { px: { x: e.clientX, y: e.clientY }, cands };
      onSelect(new Set([pick]));
      return;
    }
    const r = rectOf(d.start, d.cur);
    if (d.mode === 'draw') {
      if (r.x1 - r.x0 < MIN_BOX || r.y1 - r.y0 < MIN_BOX) return;
      const nb: Box = { id: null, ...r, score: 1, weak: false, deleted: false, added: true };
      onChange([...boxes, nb]);
      onSelect(new Set([boxes.length]));
    } else if (d.mode === 'select') {
      const hits = boxes.map((b, i) => ({ b, i })).filter(({ b }) => visible(b) && intersects(b, r)).map(({ i }) => i);
      onSelect(d.shift ? new Set([...selected, ...hits]) : new Set(hits));
    }
  };

  const kept = boxes.filter(b => !b.deleted && !b.weak).length;
  const deleted = boxes.filter(b => b.deleted).length;
  const added = boxes.filter(b => b.added && !b.deleted).length;
  const tone = pending === 'modified' ? 'bg-blue-100 ring-2 ring-blue-300'
    : pending === 'correct' ? 'bg-emerald-50 ring-2 ring-emerald-300'
      : 'bg-white ring-1 ring-border';

  return (
    <div
      onClick={onFocus}
      data-key={it.key}
      className={`relative rounded-lg p-1.5 ${tone} ${focused ? 'z-10 outline outline-4 outline-blue-600 outline-offset-2' : ''}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <Tooltip content={`${it.zone ?? ''} · ${it.camera} · frame ${it.frame_id}`}><span className="truncate">
          📍 {it.zone} · {it.rack}
        </span></Tooltip>
        <span className="shrink-0 tabular-nums">{fmtTime(it.ts)}</span>
      </div>
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
        <span>
          <span className="text-muted-foreground">{t('data.boxes.model')}:</span>{' '}
          <span className="text-rose-700">{statusRu(it.predicted_status)}</span>
          <span className="text-muted-foreground"> → {t('data.boxes.validator')}:</span>{' '}
          <span className="text-emerald-700">{statusRu(it.validated_status)}</span>
        </span>
        {it.label && (
          <Tooltip content={it.label.annotated_by ?? ''}><span className="shrink-0 rounded bg-muted px-1 text-[10px]">
            {t(`data.boxes.status.${it.label.status}`)}
          </span></Tooltip>
        )}
      </div>

      <svg
        ref={svgRef}
        viewBox={`${view.x0} ${view.y0} ${vw} ${vh}`}
        preserveAspectRatio="none"
        className="block w-full touch-none select-none rounded bg-black/5"
        style={{ aspectRatio: aspect, cursor: 'crosshair' }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={() => { drag.current = null; setBand(null); }}
      >
        {it.image_url && (
          <image href={it.image_url} x={0} y={0} width={1} height={1} preserveAspectRatio="none" />
        )}
        {/* Границы полок — только на виде всего стеллажа: на зуме они лишь шум. */}
        {shelfId == null && it.shelves.map(s => s.poly && (
          <polygon
            key={s.id}
            points={s.poly.map(p => `${p[0]},${p[1]}`).join(' ')}
            fill={s.empty ? 'rgba(239,68,68,0.10)' : 'none'}
            stroke={s.empty ? 'rgba(239,68,68,0.9)' : 'rgba(255,255,255,0.75)'}
            strokeWidth={1}
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
        ))}
        {boxes.map((b, i) => {
          if (!visible(b)) return null;
          const st = boxStyle(b, selected.has(i));
          return (
            <rect
              key={b.id ?? `n${i}`}
              x={b.x0} y={b.y0} width={Math.max(0, b.x1 - b.x0)} height={Math.max(0, b.y1 - b.y0)}
              fill={st.fill} stroke={st.stroke} strokeWidth={st.strokeWidth} strokeDasharray={st.dash}
              vectorEffect="non-scaling-stroke" pointerEvents="none"
            />
          );
        })}
        {band && (
          <rect
            x={band.x0} y={band.y0} width={band.x1 - band.x0} height={band.y1 - band.y0}
            fill="rgba(37,99,235,0.12)" stroke="#2563eb" strokeWidth={1} strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke" pointerEvents="none"
          />
        )}
      </svg>

      {/* Полки: номер и процент, пустая по модели — красным. Кнопка = зум на полку. */}
      <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
        <Tooltip content="0"><button
          type="button"
          className={`rounded px-1.5 py-0.5 ${shelfId == null ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-muted'}`}
          onClick={(e) => { e.stopPropagation(); onShelf(null); }}
        >
          {t('data.boxes.whole')}
        </button></Tooltip>
        {it.shelves.map((s, idx) => (
          <Tooltip key={s.id} content={`${idx + 1} · ${s.name} · порог ${Math.round(s.threshold * 100)}%`}><button
            type="button"
            className={`rounded px-1.5 py-0.5 tabular-nums ${shelfId === s.id ? 'bg-secondary font-medium' : 'hover:bg-muted'} ${s.empty ? 'text-rose-700' : 'text-muted-foreground'}`}
            onClick={(e) => { e.stopPropagation(); onShelf(s.id); }}
          >
            {s.num}{s.pct != null ? ` · ${Math.round(s.pct * 100)}%` : ''}
          </button></Tooltip>
        ))}
        <span className="ml-auto text-muted-foreground tabular-nums">
          {kept} {t('data.boxes.boxesN')}
          {deleted ? <span className="text-rose-700"> · −{deleted}</span> : null}
          {added ? <span className="text-purple-700"> · +{added}</span> : null}
        </span>
        {onUnlabel && (
          <button type="button" className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted"
                  onClick={(e) => { e.stopPropagation(); onUnlabel(); }}>
            {t('data.boxes.unlabel')}
          </button>
        )}
      </div>
    </div>
  );
});

// ── annotator ───────────────────────────────────────────────────────────────────

export default function BoxAnnotator() {
  const { t } = useT();
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [view, setView] = useState<BoxView>('queue');
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [cols, setCols] = useState(2);
  const [focus, setFocus] = useState(0);
  const [showWeak, setShowWeak] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  // Правки этой сессии: key → боксы. Нет ключа — карточка нетронута.
  const [edits, setEdits] = useState<Record<string, Box[]>>({});
  const history = useRef<Record<string, Box[][]>>({});
  const [marks, setMarks] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Record<string, Set<number>>>({});
  const [zoom, setZoom] = useState<Record<string, number | null>>({});
  const [dims, setDims] = useState<Record<string, Dims>>({});

  const { data: zonesResp, mutate: reloadZones } = useSWR(['box-pool-zones', days],
    () => getBoxPoolZones(days).then(r => r.data));
  const zones = zonesResp?.zones ?? [];

  const { data, isLoading, mutate } = useSWR(
    ['box-pool', zoneId ?? 0, view, days, page] as const,
    () => getBoxPoolSamples({ zone_id: zoneId, view, days, page, page_size: PAGE_SIZE }).then(r => r.data),
    { revalidateOnFocus: false, keepPreviousData: true },
  );
  const items = useMemo(() => data?.items ?? [], [data]);
  useEffect(() => { setFocus(0); }, [zoneId, view, days, page]);
  useEffect(() => { setFocus(f => Math.min(f, Math.max(0, items.length - 1))); }, [items.length]);

  // Размеры картинок — для честного аспекта окна просмотра (viewBox растянут по осям).
  useEffect(() => {
    for (const it of items) {
      if (!it.image_url || dims[it.key]) continue;
      const im = new Image();
      im.onload = () => setDims(prev => (prev[it.key] ? prev : { ...prev, [it.key]: { w: im.naturalWidth, h: im.naturalHeight } }));
      im.src = it.image_url;
    }
  }, [items, dims]);

  const boxesOf = useCallback((it: BoxItem) => edits[it.key] ?? it.boxes, [edits]);
  const isModified = useCallback((it: BoxItem) => it.key in edits && !sameBoxes(edits[it.key], it.boxes), [edits]);
  const pendingOf = useCallback((it: BoxItem): 'modified' | 'correct' | null =>
    (isModified(it) ? 'modified' : marks.has(it.key) ? 'correct' : null), [isModified, marks]);

  const change = useCallback((key: string, before: Box[], next: Box[]) => {
    (history.current[key] ??= []).push(before);
    setEdits(prev => ({ ...prev, [key]: next }));
  }, []);
  const undo = useCallback((it: BoxItem) => {
    const h = history.current[it.key];
    if (!h?.length) return;
    const prev = h.pop()!;
    setEdits(e => ({ ...e, [it.key]: prev }));
    setSelected(s => ({ ...s, [it.key]: new Set() }));
  }, []);

  const deleteSelected = useCallback((it: BoxItem) => {
    const sel = selected[it.key];
    if (!sel?.size) return;
    const cur = boxesOf(it);
    // Дорисованный бокс просто исчезает; бокс детектора помечается — его надо видеть, чтобы вернуть.
    const next = cur.map((b, i) => (sel.has(i) && !b.added ? { ...b, deleted: true } : b))
      .filter((b, i) => !(sel.has(i) && b.added));
    change(it.key, cur, next);
    setSelected(s => ({ ...s, [it.key]: new Set() }));
  }, [selected, boxesOf, change]);

  const restoreSelected = useCallback((it: BoxItem) => {
    const sel = selected[it.key];
    if (!sel?.size) return;
    const cur = boxesOf(it);
    if (!cur.some((b, i) => sel.has(i) && b.deleted)) return;
    change(it.key, cur, cur.map((b, i) => (sel.has(i) ? { ...b, deleted: false } : b)));
  }, [selected, boxesOf, change]);

  const toggleMark = useCallback((it: BoxItem) => {
    if (isModified(it)) return;
    setMarks(prev => { const n = new Set(prev); if (n.has(it.key)) n.delete(it.key); else n.add(it.key); return n; });
  }, [isModified]);

  const pendingItems = useMemo(() => items.filter(it => pendingOf(it) && it.validation_id != null),
    [items, pendingOf]);
  const untouched = useMemo(() => items.filter(it => !pendingOf(it) && it.validation_id != null
    && it.label?.status !== 'correct'), [items, pendingOf]);

  const send = useCallback(async (deltas: BoxLabelDelta[]) => {
    if (!deltas.length) return;
    setBusy(true);
    try {
      const r = await boxPoolAnnotate(deltas);
      const keys = new Set(items.filter(it => deltas.some(d => d.validation_id === it.validation_id)).map(it => it.key));
      setEdits(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.has(k))));
      setMarks(prev => new Set([...prev].filter(k => !keys.has(k))));
      for (const k of keys) delete history.current[k];
      setSaved(`✓ ${t('data.boxes.saved')} ${r.data.saved}`);
      window.setTimeout(() => setSaved(s => (s.startsWith('✓') ? '' : s)), 2500);
      await mutate();
      void reloadZones();
    } catch {
      setSaved(t('data.boxes.saveFailed'));
      window.setTimeout(() => setSaved(''), 4000);
    } finally {
      setBusy(false);
    }
  }, [items, mutate, reloadZones, t]);

  const saveAll = useCallback(() => send(pendingItems.map(it => ({
    validation_id: it.validation_id!, status: pendingOf(it)!, boxes: boxesOf(it),
  }))), [send, pendingItems, pendingOf, boxesOf]);

  /** Подтвердить страницу: нетронутые — «корректно», плюс всё, что уже ждёт сохранения. */
  const confirmPage = useCallback(() => send([
    ...pendingItems.map(it => ({ validation_id: it.validation_id!, status: pendingOf(it)!, boxes: boxesOf(it) })),
    ...untouched.map(it => ({ validation_id: it.validation_id!, status: 'correct' as const, boxes: it.boxes })),
  ]), [send, pendingItems, untouched, pendingOf, boxesOf]);

  const unlabel = useCallback((it: BoxItem) => {
    if (it.validation_id == null) return;
    void send([{ validation_id: it.validation_id, status: null }]);
  }, [send]);

  const move = useCallback((dir: 'left' | 'right' | 'up' | 'down') => {
    setFocus(f => {
      const n = items.length;
      if (!n) return 0;
      if (dir === 'right') return Math.min(n - 1, f + 1);
      if (dir === 'left') return Math.max(0, f - 1);
      if (dir === 'down') return Math.min(n - 1, f + cols);
      return Math.max(0, f - cols);
    });
  }, [items.length, cols]);

  // Карточка в фокусе видна: клавиатурный проход по странице без мыши.
  useEffect(() => {
    const it = items[focus];
    if (!it) return;
    document.querySelector(`[data-key="${it.key}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [focus, items]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
      const it = items[focus];
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.code === 'KeyS') { e.preventDefault(); void saveAll(); return; }
      if (e.code === 'ArrowRight') { e.preventDefault(); move('right'); return; }
      if (e.code === 'ArrowLeft') { e.preventDefault(); move('left'); return; }
      if (e.code === 'ArrowDown') { e.preventDefault(); move('down'); return; }
      if (e.code === 'ArrowUp') { e.preventDefault(); move('up'); return; }
      if (e.code === 'KeyW' && !mod) { setShowWeak(v => !v); return; }
      if (!it) return;
      if (e.code === 'KeyD' || e.code === 'Delete' || e.code === 'Backspace') { e.preventDefault(); deleteSelected(it); return; }
      if (e.code === 'KeyR' && !mod) { restoreSelected(it); return; }
      if (e.code === 'KeyZ') { e.preventDefault(); undo(it); return; }
      if (e.code === 'Escape') { setSelected(s => ({ ...s, [it.key]: new Set() })); return; }
      if (e.code === 'Space' || e.code === 'Enter') { e.preventDefault(); toggleMark(it); move('right'); return; }
      if (/^Digit[0-9]$/.test(e.code)) {
        const n = Number(e.code.slice(5));
        const sh = n === 0 ? null : it.shelves[n - 1]?.id;
        if (n === 0 || sh != null) setZoom(z => ({ ...z, [it.key]: sh ?? null }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, focus, move, saveAll, deleteSelected, restoreSelected, undo, toggleMark]);

  // Несохранённое не должно пропасть от случайного F5.
  useEffect(() => {
    if (!pendingItems.length) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [pendingItems.length]);

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const facets = data?.facets;
  const queueTotal = zonesResp?.totals.queue ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2">
        <Select
          className="h-8" wrapperClassName="w-auto"
          value={zoneId ?? 'all'}
          onChange={e => { const v = e.target.value; setZoneId(v === 'all' ? null : Number(v)); setPage(1); }}
        >
          <option value="all">{t('data.boxes.allZones')} ({queueTotal})</option>
          {zones.map(z => (
            <option key={z.zone_id} value={z.zone_id}>{z.zone ?? z.zone_id} ({z.queue})</option>
          ))}
        </Select>

        <Tooltip content={t('data.boxes.queueHint')}><div className="inline-flex overflow-hidden rounded-md border text-xs">
          {BOX_VIEWS.map(v => (
            <button
              key={v} type="button"
              className={`px-2.5 py-1 ${view === v
                ? (v === 'queue' ? 'bg-amber-100 text-amber-900' : v === 'modified' ? 'bg-blue-100 text-blue-900' : 'bg-emerald-100 text-emerald-900')
                : 'text-muted-foreground'}`}
              onClick={() => { setView(v); setPage(1); }}
            >
              {t(`data.boxes.view.${v}`)} ({facets?.[v] ?? 0})
            </button>
          ))}
        </div></Tooltip>

        <Select className="h-7 text-xs" wrapperClassName="w-auto" value={days}
                onChange={e => { setDays(Number(e.target.value)); setPage(1); }}>
          {DAYS_OPTIONS.map(d => <option key={d} value={d}>{d} {t('data.boxes.days')}</option>)}
        </Select>

        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomIn')}
                  disabled={cols <= MIN_COLS} onClick={() => setCols(c => Math.max(MIN_COLS, c - 1))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="w-4 text-center text-xs text-muted-foreground">{cols}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomOut')}
                  disabled={cols >= MAX_COLS} onClick={() => setCols(c => Math.min(MAX_COLS, c + 1))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>

        <Tooltip content={t('data.boxes.showWeakHint')}><label className="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={showWeak} onChange={e => setShowWeak(e.target.checked)} />
          {t('data.boxes.showWeak')}
        </label></Tooltip>

        <span className="text-[11px] text-muted-foreground">{t('data.boxes.keys')}</span>

        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs ${saved.startsWith('✓') ? 'text-emerald-600' : 'text-amber-700'}`}>{saved}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {items.length ? Math.min(focus + 1, items.length) : 0} / {items.length} · {t('data.boxes.page')} {page}/{pages} · {total}
          </span>
          <Button variant="outline" size="sm" onClick={() => { void mutate(); void reloadZones(); }} title={t('data.pool.refreshHint')}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={!items[focus] || !history.current[items[focus].key]?.length}
                  onClick={() => items[focus] && undo(items[focus])} title="Z">
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button size="sm" disabled={busy || !pendingItems.length} onClick={() => void saveAll()}
                  className={pendingItems.length ? 'bg-blue-600 hover:bg-blue-700' : ''} title="Ctrl/⌘+S">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            {t('data.boxes.save')} ({pendingItems.length})
          </Button>
          {view === 'queue' && (
            <Button size="sm" variant="outline" disabled={busy || (!untouched.length && !pendingItems.length)}
                    onClick={() => void confirmPage()} title={t('data.boxes.confirmHint')}>
              <CheckCheck className="mr-1.5 h-4 w-4" />
              {t('data.boxes.confirm')} ({untouched.length})
            </Button>
          )}
        </div>
      </div>

      {isLoading && !data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}</div>
      )}
      {data && !items.length && (
        <div className="py-10 text-center text-sm text-muted-foreground">{t('data.boxes.empty')}</div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {items.map((it, i) => (
          <BoxCard
            key={it.key}
            it={it}
            boxes={boxesOf(it)}
            selected={selected[it.key] ?? new Set()}
            shelfId={it.key in zoom ? zoom[it.key] : defaultShelf(it)}
            dims={dims[it.key]}
            focused={i === focus}
            pending={pendingOf(it)}
            showWeak={showWeak}
            onFocus={() => setFocus(i)}
            onSelect={(sel) => setSelected(s => ({ ...s, [it.key]: sel }))}
            onChange={(next) => change(it.key, boxesOf(it), next)}
            onShelf={(sid) => setZoom(z => ({ ...z, [it.key]: sid }))}
            onUnlabel={view !== 'queue' ? () => unlabel(it) : undefined}
          />
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="tabular-nums">{page} / {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
